import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { apiClient, formatApiError } from "../api/client";
import { useTranslation } from "react-i18next";

export default function ResetPasswordPage() {
  const { t } = useTranslation("auth");
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const tooShort = password.length > 0 && password.length < 8;
  const mismatch = confirm.length > 0 && password !== confirm;
  const canSubmit =
    token && password.length >= 8 && password === confirm && !submitting;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setError(null);
    setSubmitting(true);
    try {
      await apiClient.post("/auth/reset-password", {
        token,
        new_password: password,
      });
      toast.success(t("reset.success"));
      navigate("/login", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (!token) {
    return (
      <div className="mx-auto max-w-md py-12" data-testid="reset-password-page">
        <div className="rounded-3xl border border-zinc-800 bg-zinc-900/40 p-8 space-y-4 text-center">
          <h1 className="text-2xl font-semibold text-zinc-100">{t("reset.invalidTitle")}</h1>
          <p
            data-testid="reset-password-missing-token"
            className="text-sm text-zinc-400"
          >
            {t("reset.invalidBody")}
          </p>
          <Link
            to="/forgot-password"
            data-testid="reset-password-request-new"
            className="inline-block rounded-md bg-emerald-500 px-4 py-2.5 text-sm font-medium text-zinc-950 transition-colors hover:bg-emerald-400"
          >
            {t("reset.requestNew")}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-md py-12" data-testid="reset-password-page">
      <div className="rounded-3xl border border-zinc-800 bg-zinc-900/40 p-8 space-y-5">
        <header className="space-y-2">
          <h1 className="text-2xl font-semibold text-zinc-100">{t("reset.title")}</h1>
          <p className="text-sm text-zinc-400 leading-relaxed">{t("reset.subtitle")}</p>
        </header>

        <form onSubmit={onSubmit} className="space-y-4" data-testid="reset-password-form">
          <div className="space-y-1.5">
            <label
              htmlFor="reset-password"
              className="text-xs uppercase tracking-[0.18em] text-zinc-500"
            >
              {t("reset.newPassword")}
            </label>
            <input
              id="reset-password"
              type="password"
              autoComplete="new-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="reset-password-input"
              className="w-full rounded-md border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm text-zinc-100 focus:border-emerald-500 focus:outline-none"
            />
            {tooShort && (
              <p className="text-xs text-amber-300">{t("reset.tooShort")}</p>
            )}
          </div>

          <div className="space-y-1.5">
            <label
              htmlFor="reset-password-confirm"
              className="text-xs uppercase tracking-[0.18em] text-zinc-500"
            >
              {t("reset.confirmPassword")}
            </label>
            <input
              id="reset-password-confirm"
              type="password"
              autoComplete="new-password"
              required
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              data-testid="reset-password-confirm-input"
              className="w-full rounded-md border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm text-zinc-100 focus:border-emerald-500 focus:outline-none"
            />
            {mismatch && (
              <p className="text-xs text-rose-300">{t("reset.mismatch")}</p>
            )}
          </div>

          {error && (
            <div
              data-testid="reset-password-error"
              className="rounded-md border border-rose-900 bg-rose-950/40 px-3 py-2 text-sm text-rose-300"
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={!canSubmit}
            data-testid="reset-password-submit"
            className="w-full rounded-md bg-emerald-500 px-4 py-2.5 text-sm font-medium text-zinc-950 transition-colors hover:bg-emerald-400 disabled:opacity-60"
          >
            {submitting ? t("reset.saving") : t("reset.submit")}
          </button>
        </form>

        <div className="pt-2 text-center text-xs text-zinc-500">
          <Link
            to="/login"
            className="hover:text-zinc-300 underline-offset-4 hover:underline"
          >
            {t("reset.backToLogin")}
          </Link>
        </div>
      </div>
    </div>
  );
}
